import { httpsCallable } from 'firebase/functions'
import { ref, uploadBytes } from 'firebase/storage'
import { functions, storage, ensureSignedIn, isBackendConfigured } from '../firebase'
import { DISCLAIMER, NEVER_COLD, getSymptom } from '../config/constants'
import { priceRange } from './format'

// ---------------------------------------------------------------------------
// API layer. When a real Firebase project is configured we call the deployed
// Cloud Functions. When it is not (local preview / demo with no .env), we fall
// back to mock responses so the entire flow is walkable without a backend.
// The mock estimate is demo-only and intentionally simplified — the AUTHORITATIVE
// pricing + recommendation logic lives in functions/src/ and reads Firestore.
// ---------------------------------------------------------------------------

export { isBackendConfigured }

export async function uploadScanPhoto(blob) {
  if (!isBackendConfigured) {
    return { storagePath: `mock/scans/${Date.now()}.jpg` }
  }
  // Storage rules scope uploads to scans/{uid}/, so a signed-in uid is required.
  const uid = await ensureSignedIn()
  if (!uid) throw new Error('auth-unavailable') // caller falls back to manual entry
  const storagePath = `scans/${uid}/${Date.now()}.jpg`
  await uploadBytes(ref(storage, storagePath), blob, { contentType: 'image/jpeg' })
  return { storagePath }
}

export async function extractDataPlate({ imageBase64, symptom }) {
  if (!isBackendConfigured) {
    await delay(1400)
    return mockExtract()
  }
  // The vision callable requires an auth context (throttling is per-uid).
  const uid = await ensureSignedIn()
  if (!uid) throw new Error('auth-unavailable') // caller falls back to manual entry
  const fn = httpsCallable(functions, 'extractDataPlate')
  const res = await fn({ imageBase64, symptom })
  return res.data
}

export async function getEstimate({ unit, symptom }) {
  if (!isBackendConfigured) {
    await delay(600)
    return mockEstimate({ unit, symptom })
  }
  // Best-effort auth: getEstimate does NOT require an auth context, so proceed
  // even if the anonymous handshake is unavailable — never block the estimate.
  await ensureSignedIn()
  const fn = httpsCallable(functions, 'getEstimate')
  const res = await fn({ unit, symptom })
  return res.data
}

export async function createBooking(payload) {
  if (!isBackendConfigured) {
    await delay(900)
    return { jobId: `MOCK-${Math.floor(1000 + (performanceNow() % 9000))}`, mock: true }
  }
  // Best-effort auth: createBooking validates everything server-side and does
  // not require an auth context — never block a booking on the handshake.
  await ensureSignedIn()
  const fn = httpsCallable(functions, 'createBooking')
  const res = await fn(payload)
  return res.data
}

// ---------------------------------------------------------------------------
// Mocks (no-backend demo mode only)
// ---------------------------------------------------------------------------

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms))
}

// Avoid Date.now()/Math.random() reliance for the id; harmless in browser.
function performanceNow() {
  return Math.floor(typeof performance !== 'undefined' ? performance.now() : 4242)
}

function mockExtract() {
  // A believable 2015 Rheem 40-gal electric.
  return {
    brand: 'Rheem',
    model: 'XE40M06ST45U1',
    serial: 'Q091531234',
    gallons: 40,
    fuelType: 'electric',
    manufactureDate: '2015-09',
    manufactureMonth: 9,
    manufactureYear: 2015,
    ageYears: 2026 - 2015,
    decode: { confidence: 'high', source: 'Rheem 4-digit MMYY prefix', decoded: true },
    extractionConfidence: 'high',
    needsConfirmation: false,
    mock: true,
  }
}

// Demo-only estimate. Mirrors the server response SHAPE and the plan's §5.3
// recommendation rules, using fixed placeholder prices instead of Firestore.
function mockEstimate({ unit, symptom }) {
  const age = ageFromUnit(unit)
  const sym = getSymptom(symptom)
  const repairClass = sym?.repairClass || 'repairable'

  const replaceOption = mockReplaceOption(unit)
  const repairOption = mockRepairOption(symptom)
  const flushOption = mockFlushOption()

  const summary = unitSummaryText(unit, age)
  let headline, rationale, primaryType, options

  if (age >= 10) {
    primaryType = 'replace'
    headline = 'Replacement recommended'
    rationale = `At ${age} years, this unit is past its expected service life. Repairs at this age tend to be throwing good money after bad.`
    options = [{ ...replaceOption, recommended: true }]
  } else if (symptom === 'leaking') {
    primaryType = age >= 6 ? 'replace' : 'repair'
    headline = age >= 6 ? 'Likely a replacement' : "Let's find the source of that leak"
    rationale =
      "If the tank itself is leaking it can't be repaired — but a loose fitting or valve often can. Here are both, and your technician confirms the source on-site before any work begins."
    options =
      primaryType === 'replace'
        ? [{ ...replaceOption, recommended: true }, { ...repairOption, recommended: false }]
        : [{ ...repairOption, recommended: true }, { ...replaceOption, recommended: false }]
  } else if (age >= 8) {
    primaryType = repairClass === 'replace-leaning' ? 'replace' : 'repair'
    headline = 'It could go either way — here are both'
    rationale =
      "You're in the window where repair and replacement are both reasonable. Here's an honest look at each so you can decide."
    options =
      primaryType === 'replace'
        ? [{ ...replaceOption, recommended: true }, { ...repairOption, recommended: false }]
        : [{ ...repairOption, recommended: true }, { ...replaceOption, recommended: false }]
  } else if (repairClass === 'info') {
    primaryType = 'flush'
    headline = "You're in good shape"
    rationale = `This unit is only ${age} years old with plenty of life left. A maintenance flush keeps it running efficiently — no big spend needed.`
    options = [{ ...flushOption, recommended: true }]
  } else if (repairClass === 'replace-leaning' && age >= 6) {
    primaryType = 'replace'
    headline = 'Likely a replacement'
    rationale =
      'The symptom you described often points to tank corrosion. Your tech confirms on-site before any work.'
    options = [{ ...replaceOption, recommended: true }, { ...repairOption, recommended: false }]
  } else {
    primaryType = 'repair'
    headline = 'This looks repairable'
    rationale = `At ${age} years, a targeted repair is usually the smart money. We'll only steer you to replacement if it truly makes sense.`
    options = [{ ...repairOption, recommended: true }, { ...replaceOption, recommended: false }]
  }

  return {
    unitSummary: summary,
    headline,
    rationale,
    primaryType,
    options,
    factors: [
      'A drain pan if one is missing or cracked',
      'A code-required thermal expansion tank',
      'Difficult access (tight closet, attic, or garage shelf)',
      'A permit, if your municipality requires one',
    ],
    memberSavings: NEVER_COLD.replacementSavings,
    disclaimer: DISCLAIMER,
    mock: true,
    _rangeText: priceRange(options[0].totalLow, options[0].totalHigh),
  }
}

function mockReplaceOption(unit) {
  const gallons = unit?.gallons || 40
  const gas = unit?.fuelType === 'gas'
  const exactPrice = gas ? 1350 : 1200
  const laborLow = 450
  const laborHigh = 750
  return {
    type: 'replace',
    label: `${unit?.brand || 'New'} ${gallons}-gal ${gas ? 'gas' : 'electric'} — installed`,
    exactLineLabel: 'Equipment + parts',
    exactPrice,
    laborLow,
    laborHigh,
    totalLow: exactPrice + laborLow,
    totalHigh: exactPrice + laborHigh,
  }
}

function mockRepairOption(symptom) {
  const map = {
    no_hot_water: { label: 'Heating element + thermostat repair', parts: 95, lo: 150, hi: 250 },
    not_enough_hot_water: { label: 'Thermostat / dip tube repair', parts: 70, lo: 140, hi: 240 },
    strange_noises: { label: 'Tank flush + sediment service', parts: 0, lo: 129, hi: 189 },
    rusty_water: { label: 'Anode rod replacement', parts: 55, lo: 140, hi: 220 },
    leaking: { label: 'Fitting / valve repair', parts: 45, lo: 140, hi: 240 },
    just_checking: { label: 'Tank flush + inspection', parts: 0, lo: 129, hi: 189 },
  }
  const r = map[symptom] || map.no_hot_water
  return {
    type: 'repair',
    label: r.label,
    exactLineLabel: 'Parts',
    exactPrice: r.parts,
    laborLow: r.lo,
    laborHigh: r.hi,
    totalLow: r.parts + r.lo,
    totalHigh: r.parts + r.hi,
  }
}

function mockFlushOption() {
  return {
    type: 'flush',
    label: 'Maintenance flush + inspection',
    exactLineLabel: 'Parts',
    exactPrice: 0,
    laborLow: 129,
    laborHigh: 189,
    totalLow: 129,
    totalHigh: 189,
  }
}

function ageFromUnit(unit) {
  if (unit?.ageYears != null) return unit.ageYears
  if (unit?.manufactureYear) return Math.max(0, 2026 - unit.manufactureYear)
  return 8
}

function unitSummaryText(unit, age) {
  const parts = []
  if (unit?.brand) parts.push(unit.brand)
  if (unit?.gallons) parts.push(`${unit.gallons}-gallon`)
  if (unit?.fuelType && unit.fuelType !== 'unknown') parts.push(unit.fuelType)
  let s = parts.join(' ') || 'Your water heater'
  if (unit?.manufactureDate) s += `, manufactured ${prettyMonth(unit.manufactureDate)}`
  if (age != null) s += ` — ${age} years old`
  return s
}

function prettyMonth(ym) {
  // ym like "2015-09"
  const [y, m] = String(ym).split('-')
  const months = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const mi = parseInt(m, 10)
  return m && months[mi] ? `${months[mi]} ${y}` : y
}
